import React, { useCallback, useState } from "react"
import PropTypes from "prop-types"
import { Modal, Button } from "components/common"
import { useDispatch, useSelector } from "react-redux"
import { uploadTemplatePolicy } from "redux/templatePoliciesSlice"
import { useDropzone } from "react-dropzone"
import { FaUpload, FaFile, FaTimes } from "react-icons/fa"
import { toast } from "react-toastify"

const TemplatePolicyUpload = ({ show, onHide, onSuccess, parentId }) => {
  const [files, setFiles] = useState([])
  const dispatch = useDispatch()
  const { uploading } = useSelector((state) => state.template_policies)

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true
  })

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.warning("Please select at least one file to upload")
      return
    }

    const formData = new FormData()
    files.forEach((file) => {
      formData.append("documents[]", file)
    })

    try {
      await dispatch(uploadTemplatePolicy({
        assetId: parentId,
        data: formData
      })).unwrap()

      setFiles([])
      onSuccess()
    } catch (error) {
      // Error is handled by Redux slice
    }
  }

  const handleClose = () => {
    setFiles([])
    onHide()
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B"
    if (bytes < 1048576) return (bytes / 1024).toFixed(2) + " KB"
    return (bytes / 1048576).toFixed(2) + " MB"
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      title={(
        <>
          <FaUpload className="tw-mr-2" />
          Upload Files
        </>
      )}
      footer={(
        <>
          <Button variant="secondary" onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={uploading || files.length === 0}
          >
            {uploading ? "Uploading..." : `Upload ${files.length} File(s)`}
          </Button>
        </>
      )}
    >
      <div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? "active" : ""}`}
        style={{
          border: "2px dashed #ccc",
          borderRadius: "8px",
          padding: "40px",
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: isDragActive ? "#f0f8ff" : "#fafafa",
          transition: "all 0.3s ease"
        }}
      >
        <input {...getInputProps()} />
        <FaUpload size={48} className="tw-text-slate-500 tw-mb-3" />
        <p className="tw-mb-0">
          {isDragActive
            ? "Drop the files here..."
            : "Drag and drop files here, or click to select files"}
        </p>
        <small className="tw-text-slate-500">Multiple files supported</small>
      </div>

      {files.length > 0 && (
        <div className="tw-mt-4">
          <h6>Selected Files ({files.length})</h6>
          <ul className="tw-list-none tw-p-0 tw-m-0 tw-border tw-border-solid tw-border-slate-200 tw-rounded-md tw-divide-y tw-divide-slate-200">
            {files.map((file, index) => (
              <li
                key={index}
                className="tw-flex tw-justify-between tw-items-center tw-px-3 tw-py-2"
              >
                <div>
                  <FaFile className="tw-mr-2 tw-text-slate-400" />
                  <strong>{file.name}</strong>
                  <small className="tw-text-slate-500 tw-ml-2">
                    ({formatFileSize(file.size)})
                  </small>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="tw-text-red-600 tw-p-0 tw-bg-transparent tw-border-0 tw-cursor-pointer"
                  disabled={uploading}
                >
                  <FaTimes />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  )
}

TemplatePolicyUpload.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  parentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}

export default TemplatePolicyUpload
